// frontend_react/src/pages/supplierInvoice/SupplierInvoiceUploadPage.jsx

import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { uploadSupplierInvoice } from "../../services/supplierInvoiceApi";
import { useAdminScope } from "../../context/AdminScopeContext";

export default function SupplierInvoiceUploadPage() {

  const navigate = useNavigate();
  const { selectedCompanyUuid } = useAdminScope();

  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = async (file) => {

    if (!file) return;

    setError(null);
    setUploading(true);

    try {

      const formData = new FormData();
      formData.append("file", file);

      const res = await uploadSupplierInvoice(formData, {
        companyUuid: selectedCompanyUuid,
      });

      const invoiceUuid = res.data.invoice_uuid;

      navigate(`/supplier-invoices/${invoiceUuid}/validate`);

    } catch (err) {

      console.error(err);
      setError("Erreur lors de l'upload de la facture.");

    } finally {

      setUploading(false);

    }
  };

  const onDrop = async (e) => {

    e.preventDefault();
    setDragging(false);

    const file = e.dataTransfer.files?.[0];

    if (file) {
      await handleFile(file);
    }
  };

  return (
    <Page>

      <Content>

        <Title>Import facture fournisseur</Title>

        <Subtitle>
          Dépose une facture PDF ou une image.
          L’IA analysera automatiquement les lignes.
        </Subtitle>

        <Dropzone
          $dragging={dragging}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >

          {uploading ? (
            <>
              <Loader />
              <DropText>Upload et traitement en cours...</DropText>
            </>
          ) : (
            <>
              <UploadIcon>📄</UploadIcon>

              <DropText>
                Glisse ta facture ici
              </DropText>

              <Small>
                ou clique pour sélectionner un fichier
              </Small>

              <HiddenInput
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </>
          )}

        </Dropzone>

        {error && (
          <ErrorBox>
            {error}
          </ErrorBox>
        )}

      </Content>

    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  background: #0f0f0f;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
`;

const Content = styled.div`
  width: 100%;
  max-width: 700px;
`;

const Title = styled.h1`
  color: white;
  font-size: 38px;
  margin-bottom: 12px;
`;

const Subtitle = styled.p`
  color: #999;
  margin-bottom: 40px;
  line-height: 1.5;
`;

const Dropzone = styled.label`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  min-height: 320px;

  border: 2px dashed
    ${({ $dragging }) => ($dragging ? "#2563eb" : "#444")};

  background:
    ${({ $dragging }) => ($dragging ? "#111827" : "#181818")};

  border-radius: 24px;

  transition: all 0.25s ease;

  cursor: pointer;

  overflow: hidden;

  &:hover {
    border-color: #2563eb;
    background: #151515;
  }
`;

const UploadIcon = styled.div`
  font-size: 64px;
  margin-bottom: 18px;
`;

const DropText = styled.div`
  color: white;
  font-size: 24px;
  font-weight: 600;
`;

const Small = styled.div`
  margin-top: 10px;
  color: #777;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ErrorBox = styled.div`
  margin-top: 20px;
  padding: 14px;
  border-radius: 12px;
  background: #3b0d0d;
  border: 1px solid #7f1d1d;
  color: #fca5a5;
`;

const Loader = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 999px;
  border: 4px solid #333;
  border-top-color: #2563eb;

  animation: spin 1s linear infinite;

  margin-bottom: 20px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;